import { createSynth } from './synth.js';
import { createMusic } from './music.js';

/**
 * The context, the master fader, and the one gesture a browser insists on.
 *
 * Nothing here knows what a footstep is. It owns the `AudioContext`, one gain
 * node everything in the game passes through, and the settings that survive a
 * reload — and that is the whole of it.
 *
 * ---------------------------------------------------------------------------
 * NOTHING MAKES A SOUND BEFORE A GESTURE
 *
 * Not as a courtesy: a browser will not let it. An `AudioContext` made before
 * the player has touched anything starts suspended, and a game that builds its
 * whole graph on top of a suspended context and then never resumes it is a
 * game that is silent for reasons nobody can find. So the context is not
 * created at all until the first key or click, and every entry point in this
 * layer is a no-op until it exists.
 * ---------------------------------------------------------------------------
 *
 * ---------------------------------------------------------------------------
 * SETTINGS ARE NOT THE SAVE
 *
 * Their own key, deliberately. The save carries a version and refuses anything
 * written by an older build, which is right for a shift in progress and wrong
 * for a volume: wiping the week should not unmute the game, and a schema change
 * to the BEO record should not reset a fader. Two keys, two lifetimes.
 * ---------------------------------------------------------------------------
 */

export const AUDIO_KEY = 'lionandcrown.audio';

/** Where the fader sits on a machine that has never played this before. */
export const DEFAULT_VOLUME = 0.7;

export function createEngine({
  storage = globalThis.localStorage,
  key = AUDIO_KEY,
  Context = globalThis.AudioContext ?? globalThis.webkitAudioContext,
} = {}) {
  const settings = read();
  let volume = settings.volume;
  let muted = settings.muted;

  let context = null;
  let master = null;
  /** Every sound the world makes goes through this, then the master. */
  let effects = null;
  /** The score goes through this, then the master. Never the effects bus. */
  let musicBus = null;
  let music = null;
  let synth = null;
  let failed = null;
  /** The two faders under the master, set from the settings screen. */
  let effectsLevel = 1;
  let musicLevel = 0.35;

  /** Reads the two numbers, defensively, because storage is not ours. */
  function read() {
    const fallback = { volume: DEFAULT_VOLUME, muted: false };
    if (!storage) return fallback;
    try {
      const raw = storage.getItem(key);
      if (!raw) return fallback;
      const parsed = JSON.parse(raw);
      return {
        volume:
          typeof parsed?.volume === 'number' && parsed.volume >= 0 && parsed.volume <= 1
            ? parsed.volume
            : DEFAULT_VOLUME,
        muted: Boolean(parsed?.muted),
      };
    } catch {
      // Full, disabled, cleared mid-session, or something an extension left.
      return fallback;
    }
  }

  function write() {
    if (!storage) return;
    try {
      storage.setItem(key, JSON.stringify({ volume, muted }));
    } catch {
      // A setting that could not be saved is still a setting for this session.
    }
  }

  /** What the master fader should actually be at, right now. */
  const level = () => (muted ? 0 : volume);

  function apply() {
    if (!master || !context) return;
    // Ramped rather than set, because a fader that steps is a fader that clicks.
    master.gain.setTargetAtTime(level(), context.currentTime, 0.02);
  }

  return {
    /**
     * Starts the audio, if a gesture has happened and a browser will allow it.
     *
     * Safe to call on every key press for the life of the page: after the first
     * success it is a resume on an already-running context, which costs
     * nothing. Returns whether there is sound now.
     */
    unlock() {
      if (failed) return false;
      if (!Context) {
        failed = 'no AudioContext in this browser';
        return false;
      }
      try {
        if (!context) {
          context = new Context();
          master = context.createGain();
          master.gain.value = level();
          master.connect(context.destination);
          effects = context.createGain();
          effects.gain.value = effectsLevel;
          effects.connect(master);
          musicBus = context.createGain();
          musicBus.gain.value = musicLevel;
          musicBus.connect(master);
          synth = createSynth(context, { destination: effects });
          music = createMusic({ context, destination: musicBus });
          /*
           * Every voice the world plays steps the score out of its way for a
           * moment. The score must never be the reason a sound was missed.
           */
          const play = synth.play.bind(synth);
          synth.play = (...args) => {
            const voice = play(...args);
            if (voice) music?.duck();
            return voice;
          };
        }
        // Suspended is the normal state for a context made in the same tick as
        // the gesture; resume is what the gesture is for.
        if (context.state === 'suspended') context.resume?.();
        return context.state !== 'closed';
      } catch (error) {
        failed = error?.message ?? 'audio refused';
        return false;
      }
    },

    /** True once there is somewhere for a sound to go. */
    get ready() {
      return Boolean(context && synth && context.state === 'running');
    },
    /** Why there is no sound, if there is no sound. Null when it is fine. */
    get unavailable() {
      return failed;
    },
    get context() {
      return context;
    },
    get synth() {
      return synth;
    },
    /** Everything passes through this. Handed to anything that makes a voice. */
    get master() {
      return master;
    },
    /** The ambient score, once there is sound. Null before the first gesture. */
    get music() {
      return music;
    },
    /** The two faders under the master: the world's sounds, and the score. */
    setEffects(next) {
      effectsLevel = Math.max(0, Math.min(1, Number(next) || 0));
      if (effects && context) effects.gain.setTargetAtTime(effectsLevel, context.currentTime, 0.02);
      return effectsLevel;
    },
    setMusic(next) {
      musicLevel = Math.max(0, Math.min(1, Number(next) || 0));
      if (musicBus && context) musicBus.gain.setTargetAtTime(musicLevel, context.currentTime, 0.05);
      return musicLevel;
    },

    get volume() {
      return volume;
    },
    setVolume(next) {
      volume = Math.max(0, Math.min(1, Number(next) || 0));
      apply();
      write();
      return volume;
    },
    /** A step of the fader, for the two keys that have one. */
    nudge(by) {
      return this.setVolume(volume + by);
    },

    get muted() {
      return muted;
    },
    setMuted(next) {
      muted = Boolean(next);
      apply();
      write();
      return muted;
    },
    toggleMute() {
      return this.setMuted(!muted);
    },

    /**
     * Where the listener is standing and which way they are facing.
     *
     * Web Audio's listener faces -z with +y up, and a body in this project
     * faces `(sin yaw, cos yaw)`. Everything reaching the panners has already
     * been turned into the listener's own frame by `falloff.bearing`, so the
     * listener itself stays at the origin facing forward and only the sounds
     * move. One conversion, in one place, rather than two that can disagree.
     */
    orient() {
      if (!context?.listener) return;
      const listener = context.listener;
      if (listener.forwardX) {
        listener.forwardX.value = 0;
        listener.forwardY.value = 0;
        listener.forwardZ.value = -1;
        listener.upX.value = 0;
        listener.upY.value = 1;
        listener.upZ.value = 0;
        listener.positionX.value = 0;
        listener.positionY.value = 0;
        listener.positionZ.value = 0;
        return;
      }
      listener.setOrientation?.(0, 0, -1, 0, 1, 0);
      listener.setPosition?.(0, 0, 0);
    },

    /** For a page that is going away. */
    close() {
      try {
        context?.close?.();
      } catch {
        // Nothing to do about a context that will not close.
      }
      context = null;
      master = null;
      effects = null;
      musicBus = null;
      music = null;
      synth = null;
    },
  };
}
